import React, { useState, useMemo, useEffect } from "react";
import html2canvas from "html2canvas";
import ContentView from "./pages/ContentView";
import { useFirebase } from "./hooks/useFirebase";
import { useOfflineStorage } from "./hooks/useOfflineStorage"; // 👈 IMPORTED
import { i18n } from "./i18n";
import { useContentFilter } from "./hooks/useContentFilter";
import { getFilteredMessages } from "./utils/filterLogic";
import {
  Home,
  Search,
  Heart,
  Pen,
  Settings,
  Menu,
  X,
  ExternalLink,
  Download,
  Upload,
  Zap,
  ChevronLeft,
  ChevronRight,
  Moon,
  Sun,
  Languages, // ✅ NEW
  SignLanguage, // NEW
  MessageSquare, // NEW
  Music,
  Globe,
  AlphabetIndex,
  Copy, // NEW
  Share2, // NEW
} from "./components/Icons";

import { staticContent } from "./data/staticContent";
import QRCodeDisplay from "./components/QRCodeDisplay";
import { QRCodeSVG } from "qrcode.react";
import AppLogo from "./assets/logo.png";
import BannerLogo from "./assets/logo.png";
import { jsPDF } from "jspdf";
import { formatContentItem } from "./utils/contentFormatter";
import LanguageToggle from "./components/LanguageToggle";
import FloatingUtilityBar from "./components/FloatingUtilityBar";
import AudioPlayer from "./components/AudioPlayer";
import LanguageListPage from "./pages/LanguageListPage";
import SelectedContentPage from "./pages/SelectedContentPage";

import MessagesByLanguagePage from "./pages/MessagesByLanguagePage";
import FavoritesPage from "./pages/FavoritesPage";
import SearchPage from "./pages/SearchPage";
import NotesPage from "./pages/NotesPage";
import MyLibraryPage from "./pages/MyLibraryPage";
import ImportPage from "./pages/ImportPage";
import FeedbackPage from "./pages/FeedbackPage";
import SignLanguagePage from "./pages/SignLanguagePage";
import SharePage from "./pages/SharePage"; // NEW
import SelectionBadge from "./components/SelectionBadge";
import UpdateNotification from "./components/UpdateNotification";
// import SettingsPage from "./pages/SettingsPage"; // REMOVED
import StorageManagementPage from "./pages/StorageManagementPage"; // NEW
import InstallBanner from "./components/InstallBanner"; // NEW: Install Banner

// --- CONSTANTS ---
const PRIMARY_COLOR_CLASS = "bg-gradient-to-r from-brand-red to-brand-red-dark";
const ACCENT_COLOR_CLASS = "text-brand-red";
const DEFAULT_FONT_SIZE = "16px";

export default function App() {
  // --- Swipe to Close Sidebar Logic ---
  const [customBackHandler, setCustomBackHandler] = useState(null); // NEW: Allow pages to intercept Back

  // --- STATE LIFTING: Shared Offline Storage ---
  const { offlineTracks, downloadTrack, deleteTrack, clearLibrary } =
    useOfflineStorage();

  const touchStartRef = React.useRef(null);
  const touchEndRef = React.useRef(null);
  const minSwipeDistance = 50; // Minimum distance for a swipe to be registered

  const onTouchStart = (e) => {
    touchEndRef.current = null;
    touchStartRef.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e) => {
    if (!touchStartRef.current) return;
    touchEndRef.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = () => {
    if (!touchStartRef.current || !touchEndRef.current) return;
    const distance = touchStartRef.current - touchEndRef.current;
    const isLeftSwipe = distance > minSwipeDistance;
    if (isLeftSwipe) {
      setIsDrawerOpen(false);
    }
    // Reset refs
    touchStartRef.current = null;
    touchEndRef.current = null;
  };

  // --- NEW HELPER: Combine all selected programs into a single text string ---
  const getShareableContent = () => {
    const isThai = lang === "th";

    // 1. Get the list of actual message objects based on selectedPrograms
    const filteredContent = getFilteredMessages(
      staticContent,
      selectedPrograms,
    );

    if (filteredContent.length === 0) return null;

    const divider = "━━━━━━━━━━━━━━━━";

    // 2. Map the selected programs to an array of formatted strings
    const shareableItems = filteredContent.map((item) => {
      const { languageDisplay, messageTitle, trackTitle, programNumber } =
        formatContentItem(item, lang);
      const cardUrl = `https://5fi.sh/T${item.id}`;

      // Get verse text and reference
      const verseText = isThai ? item.verse_th : item.verse_en;
      let verseQuote = "";
      let verseRef = "";
      if (verseText) {
        const match = verseText.match(/^([^\.]+\d+:\d+)\s+(.+)$/);
        if (match) {
          verseRef = match[1];
          verseQuote = match[2];
        } else {
          verseQuote = verseText;
        }
      }

      // Format each message with decorative lines
      return `${divider}\n${languageDisplay} | ${messageTitle} | ${
        isThai ? "ข้อความ" : "Message"
      } #${programNumber}\n${divider}\n\n${
        isThai ? "ฟัง • แบ่งปัน • ดาวน์โหลด" : "Listen • Share • Download"
      }\nListen on 5fish: ${cardUrl}\n\n${
        verseQuote ? `${verseQuote}  ${verseRef}\n` : ""
      }`;
    });

    // 3. Combine the items with footer
    const combinedText = [
      ...shareableItems,
      `${divider}\n${
        isThai
          ? "ค้นพบภาษามากกว่า 6,000+ ภาษาที่ 5fish.mobi หรือ globalrecordings.net\nส่งความคิดเห็นไปที่: Thai@globalrecordings.net"
          : "Discover 6,000+ languages at 5fish.mobi or globalrecordings.net\nEmail any feedback to: Thai@globalrecordings.net"
      }`,
    ].join("\n\n");

    return combinedText;
  };

  // --- NEW: PDF Export (Browser Native Print) ---
  const handleDownloadSelectedPDF = () => {
    const filteredContent = getFilteredMessages(
      staticContent,
      selectedPrograms,
    );
    if (filteredContent.length === 0) {
      alert(t.select_content_first || "Please select some content first!");
      return;
    }

    const isThai = lang === "th";
    const titleText = isThai ? "รายการข้อความที่เลือก" : "Selected Messages";

    // --- NEW: Custom Filename with Timestamp ---
    const now = new Date();
    const dateStr = now.toISOString().split("T")[0]; // YYYY-MM-DD
    const timeStr = now
      .toTimeString()
      .split(" ")[0]
      .replace(/:/g, "-")
      .slice(0, 5); // HH-mm
    const filename = `TGN_Selected_Messages_${dateStr}_${timeStr}`;
    const dateText = now.toLocaleString(isThai ? "th-TH" : "en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

    // Generate HTML list items
    const listItemsHtml = filteredContent
      .map((item, index) => {
        const { languageDisplay, messageTitle, trackTitle, programNumber } =
          formatContentItem(item, lang);
        const cardUrl = `https://5fi.sh/T${item.id}`;

        return `
        <div class="message-item">
          <div class="item-header">
            <span class="item-index">${index + 1}.</span>
            <span class="item-lang">[${languageDisplay}]</span>
            <span class="item-title">${messageTitle}</span>
          </div>
          <div class="item-track">${trackTitle}</div>
          <div class="item-meta">
            Message #: ${programNumber} | <a href="${cardUrl}">${cardUrl}</a>
          </div>
        </div>
      `;
      })
      .join("");

    const printHtml = `
      <html>
        <head>
          <title>${filename}</title>
          <style>
            @page {
              size: A4;
              margin: 0.75in; /* --- CHANGED: 0.75in Margins --- */
            }
            body {
              font-family: "Sarabun", "Prompt", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
              line-height: 1.5;
              color: #333;
              max-width: 800px;
              margin: 0 auto;
              padding: 0; /* Padding handled by @page */
            }
            /* --- NEW: Header Layout with Logo --- */
            .print-header {
              display: flex;
              align-items: center;
              border-bottom: 2px solid #eee;
              padding-bottom: 10px;
              margin-bottom: 20px;
            }
            .header-logo {
              height: 50px;
              margin-right: 20px;
            }
            h1 {
              color: #003366; /* --- CHANGED: Thai Blue --- */
              margin: 0;
              font-size: 24px;
            }
            .date {
              color: #666;
              font-size: 0.9em;
              margin-left: auto; /* Push to right */
              border: none;
              padding: 10px 20px;
              border-radius: 6px;
              font-size: 16px;
              cursor: pointer;
              font-weight: bold;
            }
            .print-btn:hover {
              background: #b32d2d;
            }
          </style>
        </head>
        <body>
          <div class="control-bar no-print">
             <button class="print-btn" onclick="window.print()">🖨️ ${
               isThai ? "พิมพ์ / บันทึกเป็น PDF" : "Print / Save as PDF"
             }</button>
             <p style="margin-top:10px; font-size:0.9em; color:#666;">
               ${
                 isThai
                   ? "เลือก 'บันทึกเป็น PDF' ในหน้าต่างพิมพ์"
                   : "Choose 'Save as PDF' in the print destination."
               }
             </p>
          </div>

          <h1>${titleText}</h1>
          <div class="date">${dateText}</div>
          
          <div class="content">
            ${listItemsHtml}
          </div>

          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
              }, 500);
            };
          </script>
        </body>
      </html>
    `;

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      alert(t.allow_popups || "Please allow pop-ups to print.");
      return;
    }
    printWindow.document.open();
    printWindow.document.write(printHtml);
    printWindow.document.close();
  };
  // --- NEW: Filtered Message Helper ---
  const getSelectedContent = () => {
    // 1. Get the list of actual message objects based on selectedPrograms
    const filteredContent = getFilteredMessages(
      staticContent,
      selectedPrograms,
    );

    if (filteredContent.length === 0) {
      alert(
        t.please_select_messages ||
          "Please select some messages first by checking the boxes next to them!",
      );
      return null;
    }

    return filteredContent;
  };

  // --- NEW: Share Filtered Content (TEXT LIST) ---
  const handleShareSelected = async () => {
    // 1. Get the combined text list
    const contentToShare = getShareableContent();
    if (!contentToShare) {
      alert(t.select_content_first || "Please select some content first!");
      return;
    }

    const isThai = lang === "th";
    const shareTitle = isThai
      ? "ข่าวดี — รายการที่เลือก"
      : "Thai — Good News - Selected List";

    try {
      if (navigator.share) {
        // Use the native share sheet for the combined text
        await navigator.share({
          title: shareTitle,
          text: contentToShare,
        });
      } else {
        // Fallback: Copy to clipboard if Web Share API is not available
        await navigator.clipboard.writeText(contentToShare);
        alert(
          t.list_copied_to_clipboard || "Selected list copied to clipboard!",
        );
      }
    } catch (error) {
      console.error("Sharing selected list failed:", error);
      // Ignore user cancellation (AbortError)
      if (error.name !== "AbortError") {
        alert(t.share_failed || "Sharing failed or was cancelled.");
      }
    }
  };

  // --- NEW: Copy Filtered Content (TEXT LIST) ---
  const handleCopySelected = async () => {
    // 1. Get the combined text list
    const contentToCopy = getShareableContent();
    if (!contentToCopy) {
      alert(t.select_content_first || "Please select some content first!");
      return;
    }

    try {
      // Copy the text list directly to the clipboard
      await navigator.clipboard.writeText(contentToCopy);
      alert(t.list_copied_to_clipboard || "Selected list copied to clipboard!");
    } catch (error) {
      console.error("Copying selected list failed:", error);
      alert(t.copy_failed || "Failed to copy selected list.");
    }
  };

  // --- NEW: Download/Print Filtered Content (single language) ---
  // --- Download/Print SELECTED QR CARDS (3x3 grid, shrunk) ---
  const handleDownloadSelected = () => {
    const selectedContent = getSelectedContent();
    if (!selectedContent) return;

    // group into pages of 9 items
    const pages = [];
    for (let i = 0; i < selectedContent.length; i += 9) {
      pages.push(selectedContent.slice(i, i + 9));
    }

    const isThai = lang === "th";

    const htmlPages = pages
      .map((pageItems) => {
        const cardsHtml = pageItems
          .map((item) => {
            const title = isThai
              ? item.title_th || "ไม่มีชื่อ"
              : item.title_en || "Untitled";
            const verse = isThai ? item.verse_th || "" : item.verse_en || "";
            const languageDisplay = isThai
              ? item.langTh || ""
              : item.languageEn || "";
            const readMoreLabel = isThai
              ? "ฟัง แบ่งปัน ดาวน์โหลดที่:"
              : "Listen, Share, Download at:";
            const cardUrl = `https://5fi.sh/T${item.id}`;

            // simple QR image (200x200) for the card
            const qrImg = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
              cardUrl,
            )}`;

            return `
              <div class="qr-card">
                <div class="qr-header">
                  <div class="logo-wrap">
                    <img src="${AppLogo}" class="logo" />
                  </div>
                  <div class="header-text">
                    <div class="language">${languageDisplay}</div>
                    <div class="series">${title}</div>
                    <div class="program">Message # ${item.id}</div>
                  </div>
                </div>

          <!-- ✅ QR ABOVE VERSE -->
          <div class="qr-wrap">
            <img src="${qrImg}" class="qr-img" />
          </div>

          <!-- ✅ VERSE UNDER QR -->
          <div class="verse">
            ${verse}
          </div>

          <div class="read-more">
            ${readMoreLabel}<br />
            <span class="url">${cardUrl}</span>
          </div>
          <div class="footer">${
            t.scan_qr_tip ||
            "Scan the QR code or visit the link to access this content."
          }</div>
        </div>
      `;
          })
          .join("");

        return `
          <div class="page">
            ${cardsHtml}
          </div>
        `;
      })
      .join("");

    const printHtml = `
      <html>
        <head>
          <title>${t.export_title || "QR Cards"}</title>
          <style>
            @page {
              size: A4;
              margin: 0.3in; /* Reduced margin */
            }
            body {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
              margin: 0;
              padding: 0.25in;
              background: #f3f4f6;
            }
            /* HIDE ELEMENTS WHEN PRINTING */
            @media print {
              .no-print { display: none !important; }
              body { background: white; padding: 0; }
            }
            .page {
              page-break-after: always;
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              grid-template-rows: repeat(3, 1fr); /* Force 3 rows */
              gap: 0.2in; /* Reduced gap */
              height: 10.8in; /* Fix height to ensure 3 rows fit */
              align-content: start;
              background: white;
              padding: 0;
            }
            .page:last-child {
              page-break-after: auto;
            }
            .qr-card {
              border-radius: 12px;
              border: 1px solid #eee; /* Light border for definition */
              padding: 5px 5px 8px; /* Reduced padding */
              text-align: center;
              background: #ffffff;
              break-inside: avoid;
              display: flex;
              flex-direction: column;
              justify-content: space-between;
              height: 3.3in; /* Fixed height for 3 rows */
            }
            .qr-header {
              display: flex;
              align-items: center;
              justify-content: center;
              margin-bottom: 4px;
            }
            .logo-wrap .logo {
              width: 20px;
              height: 20px;
            }
            .header-text {
              margin-left: 6px;
              text-align: center;
            }
            .language {
              font-size: 11px;
              font-weight: 700;
              color: #111827;
              line-height: 1.1;
            }
            .series {
              font-size: 10px;
              font-weight: 700;
              color: #CC3333;
              line-height: 1.1;
            }
            .program {
              font-size: 8px;
              color: #4B5563;
            }
            .verse {
              font-size: 8px;
              font-style: italic;
              color: #374151;
              margin: 4px 4px;
              min-height: 24px;
              overflow: hidden;
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
            }
            .qr-wrap {
              background: #F9FAFB;
              border-radius: 8px;
              padding: 4px;
              margin-bottom: 4px;
              display: flex;
              justify-content: center;
            }
            .qr-img {
              width: 90px;
              height: 90px;
            }
            .read-more {
              font-size: 7px;
              color: #4B5563;
              margin-bottom: 2px;
              line-height: 1.1;
            }
            .url {
              color: #CC3333;
              word-break: break-all;
              font-weight: 600;
            }
            .footer {
              font-size: 6px;
              color: #9CA3AF;
            }
            /* Close Button Style */
            .control-bar {
              text-align: center;
              margin-bottom: 20px;
              padding: 10px;
              background: #333;
              color: white;
              border-radius: 8px;
            }
            .close-btn {
              background: #CC3333;
              color: white;
              border: none;
              padding: 10px 20px;
              border-radius: 6px;
              font-size: 16px;
              cursor: pointer;
              font-weight: bold;
            }
          </style>
        </head>
        <body>
          <div class="control-bar no-print">
             <p>Printing may not start automatically on some devices.</p>
             <button class="close-btn" onclick="window.print()">🖨️ Print Cards</button>
             <button class="close-btn" onclick="window.close()" style="background:#555; margin-left:10px;">Close Window</button>
          </div>

          ${htmlPages}

          <script>
            window.onload = function() {
              // Attempt to print automatically
              setTimeout(function() {
                window.print();
              }, 500); // Small delay to ensure images render
              
              // ❌ REMOVED THE AUTO-CLOSE TIMEOUT
              // The user must now close the window manually.
            };
          </script>
        </body>
      </html>
    `;

    const printWindow = window.open("", "_blank");
    // Mobile browser pop-up blocker check
    if (!printWindow) {
      alert("Please allow pop-ups to print your cards.");
      return;
    }
    printWindow.document.open();
    printWindow.document.write(printHtml);
    printWindow.document.close();
  };

  // --- State Management ---
  const initialLang = localStorage.getItem("appLang") || "th";
  const initialFontSize =
    localStorage.getItem("appFontSize") || DEFAULT_FONT_SIZE;

  const [lang, setLang] = useState(initialLang);
  const t = i18n[lang]; // Restored translation object
  const [fontSize, setFontSize] = useState(initialFontSize);
  const [pageStack, setPageStack] = useState([{ name: "Home" }]);
  const mainScrollRef = React.useRef(null); // 👈 NEW: main scroll container
  const [track, setTrack] = useState(null);
  const [isHoveringContent, setIsHoveringContent] = useState(false);
  const [isAudioMinimized, setIsAudioMinimized] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isBannerDismissed, setIsBannerDismissed] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isLangSearchBarVisible, setIsLangSearchBarVisible] = useState(false); // start hidden; toggle with header button

  // --- NEW: Search History State ---
  const [searchHistory, setSearchHistory] = useState(() => {
    const saved = localStorage.getItem("searchHistory");
    return saved ? JSON.parse(saved) : [];
  });

  // Save history to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("searchHistory", JSON.stringify(searchHistory));
  }, [searchHistory]);

  const addToSearchHistory = (term) => {
    if (!term || !term.trim()) return;
    const cleanTerm = term.trim();
    setSearchHistory((prev) => {
      // Remove if exists, then add to front
      const filtered = prev.filter((item) => item !== cleanTerm);
      return [cleanTerm, ...filtered].slice(0, 10); // Keep max 10
    });
  };

  const clearSearchHistory = () => {
    setSearchHistory([]);
  };

  // --- Audio Player Minimize Toggle ---
  const toggleAudioMinimize = () => {
    setIsAudioMinimized((prev) => !prev);
  };

  // --- Theme State (Dark Mode) ---
  const [theme, setTheme] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("theme") || "light";
    }
    return "light";
  });
  // Apply theme to html element (Tailwind + CSS variables)
  useEffect(() => {
    const root = window.document.documentElement;

    if (theme === "dark") {
      // Tailwind dark:
      root.classList.add("dark");
      // CSS variables dark:
      root.setAttribute("data-theme", "dark");
    } else {
      root.classList.remove("dark");
      root.removeAttribute("data-theme");
    }

    // Persist preference
    localStorage.setItem("theme", theme);
    // Compatibility with the patch guide key:
    localStorage.setItem("darkMode", String(theme === "dark"));
  }, [theme]);
  const toggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isPwaInstalled, setIsPwaInstalled] = useState(false); // Install Prompt state
  // --- NEW FUNCTION: navigateToHome ---
  const navigateToHome = () => {
    // Reset to a fresh Home page
    setPageStack([{ name: "Home", key: Date.now() }]);

    // Close the global search overlay and clear any old search text
    setIsSearchOpen(false);
    setSearchTerm("");

    // Scroll the main content area back to the very top
    if (mainScrollRef.current) {
      mainScrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
    // Extra safety: also scroll the window (in case browser uses window scroll)
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // --------------------------------------------------------------------------
  // *** FIX: Changed from array to object destructuring and passed  // --- HOOKS ---
  const { isAuthReady, userId, userData, saveUserData, error, logOut, signUp } =
    useFirebase(setLang); // Modified to include signUp
  // const { offlineTracks } = useOfflineStorage(); // REMOVED: Managed at top level

  // --- NEW: Toggle Favorite (Message) ---
  const handleToggleFavorite = (id) => {
    if (!userData) return;
    const currentFavorites = userData.favorites || [];
    let newFavorites;
    if (currentFavorites.includes(id)) {
      newFavorites = currentFavorites.filter((favId) => favId !== id);
    } else {
      newFavorites = [...currentFavorites, id];
    }
    saveUserData({ ...userData, favorites: newFavorites });
  };

  // --- NEW: Toggle Favorite Language ---
  const favoriteLanguagesRef = React.useRef([]);

  useEffect(() => {
    favoriteLanguagesRef.current = Array.isArray(userData?.favoriteLanguages)
      ? userData.favoriteLanguages
      : [];
  }, [userData?.favoriteLanguages]);

  // --- NEW: Toggle Favorite Language ---
  const handleToggleFavoriteLanguage = (stableKey) => {
    if (!userData || !stableKey) return;

    const clickedKey = String(stableKey);
    const currentFavorites = Array.isArray(favoriteLanguagesRef.current)
      ? favoriteLanguagesRef.current
      : [];

    const alreadyFavorite = currentFavorites.some(
      (key) => String(key) === clickedKey,
    );

    const newFavorites = alreadyFavorite
      ? currentFavorites.filter((key) => String(key) !== clickedKey)
      : [...currentFavorites, stableKey];

    favoriteLanguagesRef.current = newFavorites;

    saveUserData({
      ...userData,
      favoriteLanguages: newFavorites,
    });
  };

  // NEW: Global Search State
  const [searchTerm, setSearchTerm] = useState("");
  // --- NEW: Content Filter Logic ---
  const {
    selectedLangs,
    selectedPrograms,
    handleLanguageToggle,
    handleProgramToggle,
    clearSelection,
  } = useContentFilter();

  // --- Unified Share QR Modal ---
  const ShareQrModal = ({
    isOpen,
    onClose,
    title,
    subtitle,
    url,
    footerText,
  }) => {
    if (!isOpen) return null;

    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50"
        onClick={onClose}
      >
        <div
          className="bg-white p-6 rounded-lg shadow-xl max-w-sm w-full relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-5 text-gray-800"
          >
            ✕
          </button>

          {/* Title */}
          <h2 className="text-sm text-gray-600 text-center break-all mb-1">
            {title}
          </h2>

          {/* Subtitle (Language Name or Message Title) */}
          <h3 className="text-lg font-bold text-brand-red mb-2 text-center">
            {subtitle}
          </h3>

          {/* --- QR CODE DISPLAY --- */}
          <div className="flex justify-center mb-3 p-4 bg-white rounded-lg">
            <QRCodeDisplay
              url={url}
              size={200}
              fgColor="#000000"
              bgColor="#FFFFFF"
            />
          </div>

          {/* URL under the QR */}
          <p className="text-xs text-gray-600 text-center break-all mb-1">
            {footerText}
            <a
              href={url}
              className="text-brand-red underline break-all text-[10px] block mt-1"
              target="_blank"
              rel="noopener noreferrer"
            >
              {url}
            </a>
          </p>
        </div>
      </div>
    );
  };

  // --- Unified Share Modal State ---
  const [shareModalState, setShareModalState] = useState({
    isOpen: false,
    title: "",
    subtitle: "",
    url: "",
    footerText: "",
  });

  const handleCloseShareModal = () => {
    setShareModalState((prev) => ({ ...prev, isOpen: false }));
  };

  const handleShowQrForLanguage = (stableKey) => {
    const group = languageGroups.find((g) => g.stableKey === stableKey);
    if (group) {
      const name = lang === "en" ? group.displayNameEn : group.displayNameTh;

      // Construct URL (using first message's ISO or fallback)
      const firstMsg = group.messages[0];
      const iso3 = firstMsg?.iso3 || "";
      const url = `https://5fish.mobi/${iso3}`;

      setShareModalState({
        isOpen: true,
        title:
          t?.scan_qr_to_view_messages || "Scan QR to view all messages in:",
        subtitle: name,
        url: url,
        footerText: t?.language_qr_title || "",
      });
    }
  };

  const handleShowQrForMessage = (item, languageDisplayName) => {
    const title = lang === "en" ? item.title_en : item.title_th;

    // Construct URL
    let url = item.trackDownloadUrl;
    if (url) {
      if (!url.startsWith("http")) {
        url = `https://${url}`;
      }
    } else {
      url = `https://5fish.mobi/T${item.id}`;
    }

    setShareModalState({
      isOpen: true,
      title: t?.scan_qr_to_download || "Scan QR to download:",
      subtitle: `${languageDisplayName} - ${title}`,
      url: url,
      footerText: "",
    });
  };

  // Group Messages by Language (Memoized for performance)
  const languageGroups = useMemo(() => {
    const groups = {};
    staticContent.forEach((item) => {
      const stableKey = item.stableKey; // e.g., 'Central Thai'

      // Check if the item matches the global search term
      if (searchTerm) {
        const lowerSearchTerm = searchTerm.toLowerCase();
        const languageEn = item.languageEn?.toLowerCase() ?? "";
        const languageTh = item.langTh?.toLowerCase() ?? "";
        const titleEn = item.title_en?.toLowerCase() ?? "";
        const titleTh = item.title_th?.toLowerCase() ?? "";
        const verseEn = item.verse_en?.toLowerCase() ?? "";
        const verseTh = item.verse_th?.toLowerCase() ?? "";

        const matches =
          languageEn.includes(lowerSearchTerm) ||
          languageTh.includes(lowerSearchTerm) ||
          titleEn.includes(lowerSearchTerm) ||
          titleTh.includes(lowerSearchTerm) ||
          verseEn.includes(lowerSearchTerm) ||
          verseTh.includes(lowerSearchTerm);

        if (!matches) {
          return; // Skip this item if it doesn't match the search
        }
      }

      if (!groups[stableKey]) {
        groups[stableKey] = {
          stableKey,
          displayNameEn: item.languageEn,
          displayNameTh: item.langTh,
          langId: item.langId,
          count: 0,
          messages: [],
          programIds: new Set(),
        };
      }

      // --- DATA FLATTENING & CLEANUP ---
      // 1. Check if this Program ID has already been added to this group
      const progId = item.programId || item.id;
      if (groups[stableKey].programIds.has(progId)) {
        return; // SKIP duplicates (tracks)
      }

      // 2. Mark this Program ID as seen
      groups[stableKey].programIds.add(progId);

      // 3. Clean the Title (Remove "(Mxxx)" suffix)
      // Create a shallow copy to avoid mutating the original staticContent if needed,
      // but for display purposes in this list, we can modify a copy.
      const cleanedItem = { ...item };
      if (cleanedItem.title_en) {
        cleanedItem.title_en = cleanedItem.title_en
          .replace(/\s*\(M\d+\)/, "")
          .trim();
      }
      if (cleanedItem.title_th) {
        cleanedItem.title_th = cleanedItem.title_th
          .replace(/\s*\(M\d+\)/, "")
          .trim();
      }

      // 4. Add the unique, cleaned message
      groups[stableKey].count += 1;
      groups[stableKey].messages.push(cleanedItem);
    });

    // Convert object to array and sort by display name in the current language
    return Object.values(groups).sort((a, b) => {
      const nameA = lang === "en" ? a.displayNameEn : a.displayNameTh;
      const nameB = lang === "en" ? b.displayNameEn : b.displayNameTh;
      return nameA.localeCompare(nameB, lang);
    });
  }, [lang, searchTerm]); // Recalculate if language or searchTerm changes

  // Memoize the flat list of content for current language message view
  const currentMessageList = useMemo(() => {
    const currentPage = pageStack[pageStack.length - 1];
    if (currentPage.name === "MessagesByLanguage") {
      const group = languageGroups.find((g) => g.stableKey === currentPage.key);
      return group ? group.messages : [];
    }
    return [];
  }, [pageStack, languageGroups]);

  // Memoize the total flat list of content (used for navigation in message view)
  const flatContentList = useMemo(() => {
    // Only generate the full list when viewing a specific language
    const currentPage = pageStack[pageStack.length - 1];
    if (
      currentPage.name === "ContentView" &&
      currentPage.sourceList === "language"
    ) {
      // The item ID is currentPage.key. Find the group it belongs to.
      const targetItem = staticContent.find((i) => i.id === currentPage.key);
      if (!targetItem) return [];

      const targetGroup = languageGroups.find(
        (g) => g.stableKey === targetItem.stableKey,
      );
      return targetGroup ? targetGroup.messages : [];
    } else if (
      currentPage.name === "ContentView" &&
      currentPage.sourceList === "search"
    ) {
      // Re-use the search page's filtering logic
      if (!searchTerm) return [];
      const lowerSearchTerm = searchTerm.toLowerCase();

      return staticContent.filter((item) => {
        const languageEn = item.languageEn?.toLowerCase() ?? "";
        const languageTh = item.langTh?.toLowerCase() ?? "";
        const titleEn = item.title_en?.toLowerCase() ?? "";
        const titleTh = item.title_th?.toLowerCase() ?? "";
        const verseEn = item.verse_en?.toLowerCase() ?? "";
        const verseTh = item.verse_th?.toLowerCase() ?? "";

        return (
          languageEn.includes(lowerSearchTerm) ||
          languageTh.includes(lowerSearchTerm) ||
          titleEn.includes(lowerSearchTerm) ||
          titleTh.includes(lowerSearchTerm) ||
          verseEn.includes(lowerSearchTerm) ||
          verseTh.includes(lowerSearchTerm)
        );
      });
    }
    // Fallback for other contexts (e.g., favorites or direct content view)
    return staticContent;
  }, [pageStack, languageGroups, searchTerm]);

  // --- Navigation and State Logic ---

  const navigateTo = (pageName, key = null, sourceList = null) => {
    setPageStack((prev) => [...prev, { name: pageName, key, sourceList }]);
    setIsDrawerOpen(false);

    // Keep the search bar open when we are navigating to Search.
    // Close it for every other page.
    if (pageName !== "Search") {
      setIsSearchOpen(false);
    }

    // Always jump to the top of the main content when navigating
    if (mainScrollRef.current) {
      mainScrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
    window.scrollTo({ top: 0, behavior: "smooth" });

    // Clear search term when navigating away from Home/Search
    if (pageName !== "Search" && pageName !== "Home") {
      setSearchTerm("");
    }
  };

  // ✅ PASTE YOUR NEW FUNCTION HERE:
  const navigateToSelectedContent = () => {
    setPageStack((prev) => [...prev, { name: "SelectedContent" }]);
    setSearchTerm(""); // Clear search just in case
    setIsDrawerOpen(false); // Close sidebar if open
    setIsSearchOpen(false); // Close search bar
  };

  const goBack = () => {
    if (customBackHandler) {
      customBackHandler();
      return;
    }

    // Leaving Search should also clear the old search text so Home is not secretly filtered.
    if (currentPage.name === "Search") {
      setSearchTerm("");
    }
    setIsSearchOpen(false);

    if (pageStack.length > 1) {
      setPageStack((prev) => prev.slice(0, -1));
    } else {
      // Safety net: If we try to go back from the root, reset to Home.
      setPageStack([{ name: "Home" }]);
    }
  };

  const goForward = () => {
    // Not used for navigation stack but kept for consistency
    console.log("Forward navigation not implemented in stack model.");
  };

  const hasPrev = pageStack.length > 1;
  const hasNext = false; // Always false in this stack implementation

  // Handler for Language Card click
  const handleSelectLanguage = (stableKey) => {
    navigateTo("MessagesByLanguage", stableKey);
  };

  // Handler for Message Card click
  // Handler for Message Card click
  const handleSelectMessage = (item, sourceList = "language") => {
    navigateTo("ContentView", item.id, sourceList);
  };

  // Handler for Content View navigation (Next/Prev)
  const handleNextPrevMessage = (direction) => {
    const currentPage = pageStack[pageStack.length - 1];
    if (currentPage.name !== "ContentView") return;

    const currentItemId = currentPage.key;
    const currentIndex = flatContentList.findIndex(
      (item) => item.id === currentItemId,
    );

    let newIndex;
    if (direction === "next" && currentIndex < flatContentList.length - 1) {
      newIndex = currentIndex + 1;
    } else if (direction === "prev" && currentIndex > 0) {
      newIndex = currentIndex - 1;
    } else {
      return; // Cannot move
    }

    const newItem = flatContentList[newIndex];
    // Replace current item in stack instead of pushing a new one
    setPageStack((prev) => {
      const newStack = [...prev];
      newStack[newStack.length - 1] = {
        name: "ContentView",
        key: newItem.id,
        sourceList: currentPage.sourceList,
      };
      return newStack;
    });
    setTrack(newItem); // Auto-play next track if audio player is active
  };

  // Handler for Content Card play button
  const handlePlayMessage = (item) => {
    setTrack(item);
    setIsAudioMinimized(false); // Open the audio player when play is clicked
  };

  // --- NEW: PWA Install Click Handler ---

  // --- Current Content and Navigation Status ---
  const currentPage = pageStack[pageStack.length - 1];
  const currentItem =
    currentPage.name === "ContentView"
      ? staticContent.find((item) => item.id === currentPage.key)
      : null;

  // Show the search bar whenever the user opened it OR when we are on Search page
  const shouldShowSearchBar = isSearchOpen || currentPage.name === "Search";

  // Open search from the header search icon
  const openSearchPage = () => {
    setIsSearchOpen(true);
    setIsDrawerOpen(false);

    if (currentPage.name !== "Search") {
      navigateTo("Search");
    }
  };

  // Clear search text, return to Home, but keep the search box visible and ready
  const clearSearchAndStayOpen = () => {
    setSearchTerm("");
    setIsSearchOpen(true);
    setPageStack([{ name: "Home", key: Date.now() }]);

    if (mainScrollRef.current) {
      mainScrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Close the search bar completely and return to Home
  const closeSearchCompletely = () => {
    setSearchTerm("");
    setIsSearchOpen(false);
    setPageStack([{ name: "Home", key: Date.now() }]);

    if (mainScrollRef.current) {
      mainScrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Real form submit so Android keyboard Enter/Search actually works
  const submitSearch = (e) => {
    if (e) e.preventDefault();

    const cleanTerm = searchTerm.trim();

    if (!cleanTerm) {
      clearSearchAndStayOpen();
      return;
    }

    setSearchTerm(cleanTerm);
    setIsSearchOpen(true);
    addToSearchHistory(cleanTerm);

    if (currentPage.name !== "Search") {
      navigateTo("Search");
    }
  };

  // Shared Search handler (used by header + floating bar)
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setIsSearchOpen(true);

    if (value.trim()) {
      // Move to the Search page only once
      if (currentPage.name !== "Search") {
        navigateTo("Search");
      }
    } else {
      // If the search box is empty, stay open but show Home again
      if (currentPage.name === "Search") {
        setPageStack([{ name: "Home", key: Date.now() }]);
      }
    }
  };

  // Determine current index and next/prev status for ContentView
  const currentItemIndex = currentItem
    ? flatContentList.findIndex((item) => item.id === currentItem.id)
    : -1;
  const canGoPrev = currentItemIndex > 0;
  const canGoNext =
    currentItemIndex !== -1 && currentItemIndex < flatContentList.length - 1;

  // --- Effects ---

  // Splash Screen Timer
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000); // 2 seconds
    return () => clearTimeout(timer);
  }, []);

  // URL Parameter Effect
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const langKey = urlParams.get("langKey");

    if (langKey) {
      const decodedLangKey = decodeURIComponent(langKey);
      const group = languageGroups.find((g) => g.stableKey === decodedLangKey);

      if (group) {
        setPageStack([
          { name: "Home" },
          { name: "MessagesByLanguage", key: decodedLangKey },
        ]);

        // --- REMOVED window.history.replaceState(...) ---
        // By removing the replaceState, the browser history remains intact,
        // allowing the <Back button to function correctly.
      }
    }
    // ... rest of useEffect
  }, [languageGroups]); // Depend on languageGroups to ensure data is loaded

  // --- NEW: PWA Install Prompt Listener & Standalone Detection ---
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later.
      setDeferredPrompt(e);
      console.log("beforeinstallprompt event captured.");
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    // Check if already installed
    if (
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true
    ) {
      setIsPwaInstalled(true);
    }

    // Listen for mode changes
    const mediaQueryLocal = window.matchMedia("(display-mode: standalone)");
    const handleChange = (e) => setIsPwaInstalled(e.matches);

    try {
      mediaQueryLocal.addEventListener("change", handleChange);
    } catch (e) {
      // Create a fallback for Safari < 14
      mediaQueryLocal.addListener(handleChange);
    }

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
      try {
        mediaQueryLocal.removeEventListener("change", handleChange);
      } catch (e) {
        mediaQueryLocal.removeListener(handleChange);
      }
    };
  }, []);

  // --- NEW: PWA Install Click Handler ---
  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Show the install prompt (Android / Chrome Desktop)
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        console.log("User accepted the PWA install prompt");
      } else {
        console.log("User dismissed the PWA install prompt");
      }
      setDeferredPrompt(null);
      setIsDrawerOpen(false);
    } else {
      // iOS / Safari / Existing PWA logic where prompt isn't supported
      // Show instructions instead of false "Installed"
      const isIOS =
        /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      if (isIOS) {
        alert(
          lang === "th"
            ? "ในการติดตั้ง: แตะปุ่มแชร์ด้านล่าง แล้วเลือก 'เพิ่มไปยังหน้าจอหลัก'"
            : "To install: Tap the Share button below and select 'Add to Home Screen'",
        );
      } else {
        // Fallback for other browsers
        alert(
          lang === "th"
            ? "ติดตั้งแอพนี้ได้จากเมนูของเบราว์เซอร์ (เพิ่มไปยังหน้าจอหลัก)"
            : "Install this app from your browser menu (Add to Home Screen)",
        );
      }
    }
  };

  // --- ADDED: New Global Font Size Effect ---
  useEffect(() => {
    // Apply the selected font size to the root <html> element
    // This will scale all rem-based Tailwind classes.
    if (fontSize) {
      document.documentElement.style.fontSize = fontSize;
      // We also consolidate saving to localStorage here
      localStorage.setItem("appFontSize", fontSize);
    }
  }, [fontSize]); // Rerun this effect whenever fontSize changes
  // --- END of new block ---

  // *** FIX: Added Firebase Loading Check ***
  // If the app is not ready (Firebase has not loaded user state), show a loading screen.
  if (!isAuthReady && !isLoading) {
    // Only show auth loading AFTER splash screen
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8 bg-gray-100">
        <Zap className="w-12 h-12 text-brand-red animate-pulse mb-4" />
        <p className="text-xl font-semibold text-gray-700">
          {t.loading_app || "Loading application and connecting..."}
        </p>
        {error && <p className="mt-4 text-red-500 text-sm">Error: {error}</p>}
      </div>
    );
  }

  // --- Render Logic ---
  let PageContent;
  switch (currentPage.name) {
    case "Home":
      PageContent = (
        <LanguageListPage
          lang={lang}
          t={t}
          onSelectLanguage={handleSelectLanguage}
          languageGroups={languageGroups}
          onShowQrForLanguage={handleShowQrForLanguage}
          // --- NEW WIRING ---
          selectedPrograms={selectedPrograms}
          onToggleLanguage={handleLanguageToggle}
          onHoverChange={setIsHoveringContent} // 👈 GIVE IT THE CONTROLLER
          userData={userData} // 👇 NEW
          onToggleFavoriteLanguage={handleToggleFavoriteLanguage} // 👇 NEW
          isSearchBarVisible={isLangSearchBarVisible}
          onToggleSearchBar={setIsLangSearchBarVisible}
          key={currentPage.key || "home"} // Force remount on Home click
        />
      );
      break;

    case "SelectedContent":
      PageContent = (
        <SelectedContentPage
          lang={lang}
          t={t}
          onBack={goBack}
          onForward={goForward}
          hasPrev={hasPrev}
          hasNext={hasNext}
          selectedPrograms={selectedPrograms}
          languageGroups={languageGroups}
          allMessages={staticContent}
          onClearSelection={clearSelection}
          onShare={handleShareSelected}
          onCopy={handleCopySelected}
          onDownload={handleDownloadSelected} // This is the "Print" button (using Download icon)
          onDownloadPDF={handleDownloadSelectedPDF} // This is the actual PDF button
          userData={userData}
          onToggleFavorite={handleToggleFavorite}
        />
      );
      break;

    // --- THIS IS THE MISSING BLOCK THAT FIXES THE 404 ERROR ---
    case "ContentView":
      PageContent = (
        <ContentView
          item={currentItem}
          lang={lang}
          t={t}
          onBack={goBack}
          onForward={() => handleNextPrevMessage("next")}
          hasPrev={hasPrev}
          hasNext={hasNext}
          userData={userData}
          saveUserData={saveUserData}
          onPlay={handlePlayMessage}
          pageStack={pageStack}
        />
      );
      break;

    case "MessagesByLanguage":
      PageContent = (
        <MessagesByLanguagePage
          lang={lang}
          t={t}
          selectedLanguageKey={currentPage.key}
          onBack={goBack}
          onForward={goForward}
          hasPrev={hasPrev}
          hasNext={hasNext}
          onSelectMessage={(item) => handleSelectMessage(item, "language")}
          currentMessageList={currentMessageList}
          languageGroups={languageGroups}
          pageStack={pageStack}
          // --- NEW WIRING ---
          selectedPrograms={selectedPrograms}
          onToggleProgram={handleProgramToggle}
          onShowQrForMessage={handleShowQrForMessage} // 👈 PASSED DOWN
          userData={userData}
          onToggleFavorite={handleToggleFavorite}
        />
      );
      break;

    case "Search":
      PageContent = (
        <SearchPage
          lang={lang}
          t={t}
          onSelect={(item) => handleSelectMessage(item, "search")}
          searchTerm={searchTerm}
          onBack={goBack}
          onForward={goForward}
          hasPrev={hasPrev}
          hasNext={hasNext}
          // --- FIX: ADDED pageStack PROP ---
          pageStack={pageStack}
          // --- NEW: Search History Props ---
          searchHistory={searchHistory}
          onClearHistory={clearSearchHistory}
          onHistorySelect={(term) => {
            setSearchTerm(term);
            addToSearchHistory(term); // Refresh position
          }}
          userData={userData}
          onToggleFavorite={handleToggleFavorite}
          onOpenSearch={() => setIsSearchOpen(true)} // Auto-open search bar
        />
      );
      break;

    case "Favorites":
      PageContent = (
        <FavoritesPage
          lang={lang}
          t={t}
          userData={userData}
          onSelect={(item) => handleSelectMessage(item, "favorite")}
          setLang={setLang}
          fontSize={fontSize}
          setFontSize={setFontSize}
          onBack={goBack}
          onForward={goForward}
          hasPrev={hasPrev}
          hasNext={hasNext}
          pageStack={pageStack}
          onToggleFavorite={handleToggleFavorite}
          // 🔴 NEW: give FavoritesPage what it needs for LANGUAGES
          languageGroups={languageGroups}
          onToggleFavoriteLanguage={handleToggleFavoriteLanguage}
          onSelectLanguage={(stableKey) =>
            navigateTo("MessagesByLanguage", stableKey)
          }
          onGoHome={() => navigateTo("Home")}
        />
      );
      break;

    case "Notes":
      PageContent = (
        <NotesPage
          lang={lang}
          t={t}
          onBack={goBack}
          onForward={goForward}
          hasPrev={hasPrev}
          hasNext={hasNext}
          userData={userData}
          saveUserData={saveUserData}
        />
      );
      break;

    case "MyLibrary":
      PageContent = (
        <MyLibraryPage
          lang={lang}
          t={t}
          onBack={goBack}
          onForward={goForward}
          hasPrev={hasPrev}
          hasNext={hasNext}
          onPlay={handlePlayMessage}
          onGoHome={navigateToHome}
          // Shared Storage Props
          offlineTracks={offlineTracks}
          deleteTrack={deleteTrack}
          clearLibrary={clearLibrary}
        />
      );
      break;

    // case "Settings": // REMOVED
    //   PageContent = (
    //     <SettingsPage
    //       lang={lang}
    //       t={t}
    //       setLang={setLang}
    //       fontSize={fontSize}
    //       setFontSize={setFontSize}
    //       onBack={goBack}
    //       onForward={goForward}
    //       hasPrev={hasPrev}
    //       hasNext={hasNext}
    //       onNavigate={navigateTo}
    //     />
    //   );
    //   break;

    case "StorageManagement": // NEW ROUTE
      PageContent = (
        <StorageManagementPage
          lang={lang}
          t={t}
          onBack={goBack}
          onForward={goForward}
          hasPrev={hasPrev}
          hasNext={hasNext}
          offlineTracks={offlineTracks}
          deleteTrack={deleteTrack}
          clearLibrary={clearLibrary}
        />
      );
      break;

    case "Import":
      PageContent = (
        <ImportPage
          lang={lang}
          t={t}
          onBack={goBack}
          onForward={goForward}
          hasPrev={hasPrev}
          hasNext={hasNext}
          setCustomBackHandler={setCustomBackHandler}
          onNavigate={navigateTo} // 👈 NEW
          // Shared Storage Props
          offlineTracks={offlineTracks}
          downloadTrack={downloadTrack}
        />
      );
      break;

    case "SignLanguage": // NEW
      PageContent = (
        <SignLanguagePage
          lang={lang}
          t={t}
          onBack={goBack}
          onForward={goForward}
          hasPrev={hasPrev}
          hasNext={hasNext}
        />
      );
      break;

    case "Feedback": // NEW
      PageContent = (
        <FeedbackPage
          lang={lang}
          t={t}
          onBack={goBack}
          onForward={goForward}
          hasPrev={hasPrev}
          hasNext={hasNext}
        />
      );
      break;

    case "Share": // NEW
      PageContent = (
        <SharePage
          lang={lang}
          t={t}
          onBack={goBack}
          onForward={goForward}
          hasPrev={hasPrev}
          hasNext={hasNext}
        />
      );
      break;

    default:
      PageContent = (
        <div className="p-4 pt-8 text-center text-red-500">
          404: Page not found.
          <button
            onClick={() => setPageStack([{ name: "Home" }])}
            className="mt-4 p-2 bg-red-100 rounded"
          >
            Go Home
          </button>
        </div>
      );
  }

  // Determine if the search bar should be fully visible or just an icon
  const isSearchPage = currentPage.name === "Search";

  // --- START OF MAIN RETURN ---
  return (
    // 💡 CONDITIONAL RENDER: Show Splash Screen OR the App
    isLoading ? (
      // 1. --- SPLASH SCREEN COMPONENT (Visible while isLoading is TRUE) ---
      <div
        className={`fixed inset-0 flex items-center justify-center bg-black z-50`}
      >
        <img
          src={AppLogo} // Your optimized square logo source
          alt={t.app_name || "App Loading"}
          className="
            w-72 h-72 
            lg:w-96 lg:h-96 
            rounded-full shadow-2xl 
            object-cover
            brightness-125
            animate-windy
          "
        />
      </div>
    ) : (
      // 2. --- NORMAL APPLICATION START (Visible while isLoading is FALSE) ---
      <div className="min-h-screen bg-gray-100 dark:bg-[#374151] flex flex-col">
        {/* --- UPDATE NOTIFICATION BANNER --- */}
        <UpdateNotification />

        {/* --- UNIFIED SHARE QR MODAL --- */}
        <ShareQrModal
          isOpen={shareModalState.isOpen}
          onClose={handleCloseShareModal}
          title={shareModalState.title}
          subtitle={shareModalState.subtitle}
          url={shareModalState.url}
          footerText={shareModalState.footerText}
        />

        {/* --- HEADER (Banner) --- */}
        <header
          className={`sticky top-0 w-full ${PRIMARY_COLOR_CLASS} shadow-lg z-40 rounded-b-xl md:py-3 md:px-1`}
        >
          {/* Mobile/Tablet: 3-column grid layout */}
          <div className="grid grid-cols-3 items-center h-20 md:hidden px-1">
            {/* LEFT: Menu + Logo + Install */}
            <div className="flex items-center justify-start space-x-1">
              <div className="relative">
                <button
                  onClick={() => setIsDrawerOpen(true)}
                  className="text-white px-1 rounded-lg hover:bg-red-800 transition-colors btn-hover flex items-center"
                  aria-label="Open Sidebar Menu"
                >
                  <Menu className="w-6 h-6" />
                </button>
                <SelectionBadge
                  selectionCount={selectedPrograms.length}
                  isHovering={isHoveringContent}
                  navigateToSelectedContent={navigateToSelectedContent}
                  t={t}
                  lang={lang}
                />
              </div>
              <a
                href="https://5fish.mobi/th?r=Asia&country=Thailand"
                target="_blank"
                rel="noopener noreferrer"
                title="5fish.mobi/th?r=Asia&country=Thailand"
                className="flex items-center text-white rounded-lg hover:bg-red-800 transition-colors flex-shrink-0 -ml-1"
              >
                <img
                  src={BannerLogo}
                  alt={t.app_name}
                  className="w-auto flex-shrink-0 object-cover rounded-lg brightness-125 animate-windy"
                  style={{ height: "4rem" }}
                />
              </a>
              <button
                onClick={navigateToHome}
                className="text-white px-1 rounded-lg hover:bg-red-800 transition-colors btn-hover flex items-center"
                title={t.home || "Home"}
                aria-label={t.home || "Home"}
              >
                <Home className="w-5 h-5" />
              </button>
            </div>

            {/* CENTER: Navigation Controls */}
            <div className="flex items-center justify-center space-x-1">
              {currentPage.name !== "Home" && (
                <>
                  <button
                    onClick={goBack}
                    disabled={!hasPrev}
                    className={`p-1 rounded-lg transition-colors flex items-center ${
                      hasPrev
                        ? "text-white hover:bg-white/20"
                        : "text-red-200 opacity-50 cursor-not-allowed"
                    }`}
                    title={t.back || "Back"}
                  >
                    <ChevronLeft className="w-8 h-8" />
                  </button>
                  <button
                    onClick={goForward}
                    disabled={!hasNext}
                    className={`p-1 rounded-lg transition-colors flex items-center ${
                      hasNext
                        ? "text-white hover:bg-white/20"
                        : "text-red-200 opacity-50 cursor-not-allowed"
                    }`}
                    title={t.forward || "Forward"}
                  >
                    <ChevronRight className="w-8 h-8" />
                  </button>
                </>
              )}
            </div>

            {/* RIGHT MOBILE: Controls */}
            <div className="flex items-center justify-end space-x-1">
              <LanguageToggle lang={lang} setLang={setLang} t={t} />

              <FloatingUtilityBar
                t={t}
                lang={lang}
                setLang={setLang}
                selectionCount={selectedPrograms.length}
                onClearSelection={clearSelection}
                fontSize={fontSize}
                setFontSize={setFontSize}
                navigateToSelectedContent={navigateToSelectedContent}
              />

              {!isPwaInstalled && (
                <button
                  onClick={handleInstallClick}
                  title={t.install_app || "Install App"}
                  className="p-1 rounded-lg transition-colors btn-hover text-white hover:bg-red-800"
                  aria-label={t.install_app || "Install App"}
                >
                  <Download className="w-6 h-6" />
                </button>
              )}

              {/* A-Z / Thai alphabet side rolodex toggle - only on Home page */}
              {currentPage.name === "Home" && (
                <button
                  onClick={() => setIsLangSearchBarVisible((prev) => !prev)}
                  className="text-white p-1 rounded-lg hover:bg-red-800 transition-colors btn-hover"
                  title={
                    isLangSearchBarVisible
                      ? lang === "th"
                        ? "ซ่อนรายการตัวอักษร"
                        : "Hide A-Z index"
                      : lang === "th"
                        ? "แสดงรายการตัวอักษร"
                        : "Show A-Z index"
                  }
                  aria-label={
                    isLangSearchBarVisible
                      ? lang === "th"
                        ? "ซ่อนรายการตัวอักษร"
                        : "Hide A-Z index"
                      : lang === "th"
                        ? "แสดงรายการตัวอักษร"
                        : "Show A-Z index"
                  }
                >
                  <AlphabetIndex className="w-9 h-9" />
                </button>
              )}

              <button
                onClick={openSearchPage}
                className="text-white p-1 rounded-lg hover:bg-red-800 transition-colors btn-hover"
                aria-label="Open Search"
                title={lang === "th" ? "ค้นหา" : "Search"}
              >
                <Search className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Desktop: Flexbox layout */}
          <div className="hidden md:flex justify-between items-center relative">
            {/* Left: Menu & Logo */}
            <div className="flex items-center">
              <div className="relative mr-3">
                <button
                  onClick={() => setIsDrawerOpen(true)}
                  className="text-white p-1 rounded-lg hover:bg-red-800 transition-colors btn-hover"
                  aria-label="Open Sidebar Menu"
                >
                  <Menu className="w-6 h-6 md:w-7 md:h-7" />
                </button>
                <SelectionBadge
                  selectionCount={selectedPrograms.length}
                  isHovering={isHoveringContent}
                  navigateToSelectedContent={navigateToSelectedContent}
                  t={t}
                  lang={lang}
                />
              </div>
              <a
                href="https://5fish.mobi/th?r=Asia&country=Thailand"
                target="_blank"
                rel="noopener noreferrer"
                title="5fish.mobi/th?r=Asia&country=Thailand"
                className="flex items-center text-white rounded-lg hover:bg-red-800 transition-colors focus:outline-none focus:ring-2 focus:ring-white flex-shrink-0 -ml-1"
              >
                <img
                  src={BannerLogo}
                  alt={t.app_name}
                  className="w-auto flex-shrink-0 rounded-lg brightness-125 animate-windy"
                  style={{ height: "4rem" }}
                />
              </a>
              <button
                onClick={() => {
                  navigateToHome();
                  setIsDrawerOpen(false);
                }}
                className="text-white p-1 rounded-lg hover:bg-red-800 transition-colors btn-hover ml-2"
                title={t.home || "Home"}
                aria-label={t.home || "Home"}
              >
                <Home className="w-5 h-5" />
              </button>
            </div>

            {/* Center: Navigation Buttons */}
            <div className="flex items-center justify-center space-x-2">
              {currentPage.name !== "Home" && (
                <>
                  <button
                    onClick={goBack}
                    disabled={!hasPrev}
                    className={`p-1 rounded-lg transition-colors flex items-center ${
                      hasPrev
                        ? "text-white hover:bg-white/20"
                        : "text-red-200 opacity-50 cursor-not-allowed"
                    }`}
                    title={t.back || "Back"}
                  >
                    <ChevronLeft className="w-8 h-8" />
                  </button>
                  <button
                    onClick={goForward}
                    disabled={!hasNext}
                    className={`p-1 rounded-lg transition-colors flex items-center ${
                      hasNext
                        ? "text-white hover:bg-white/20"
                        : "text-red-200 opacity-50 cursor-not-allowed"
                    }`}
                    title={t.forward || "Forward"}
                  >
                    <ChevronRight className="w-8 h-8" />
                  </button>
                </>
              )}
            </div>

            {/* Right Desktop: Controls */}
            <div className="flex items-center space-x-4 flex-shrink-0">
              <FloatingUtilityBar
                t={t}
                lang={lang}
                setLang={setLang}
                selectionCount={selectedPrograms.length}
                onClearSelection={clearSelection}
                fontSize={fontSize}
                setFontSize={setFontSize}
                navigateToSelectedContent={navigateToSelectedContent}
                isHovering={isHoveringContent}
              />

              <LanguageToggle lang={lang} setLang={setLang} t={t} />

              {!isPwaInstalled && (
                <button
                  onClick={handleInstallClick}
                  title={t.install_app || "Install App"}
                  className="p-1 rounded-lg transition-colors btn-hover text-white hover:bg-red-800"
                  aria-label={t.install_app || "Install App"}
                >
                  <Download className="w-6 h-6" />
                </button>
              )}

              {/* A-Z / Thai alphabet side rolodex toggle - only on Home page */}
              {currentPage.name === "Home" && (
                <button
                  onClick={() =>
                    setIsLangSearchBarVisible((prev) => {
                      const next = !prev;

                      if (!prev && mainScrollRef.current) {
                        mainScrollRef.current.scrollTo({
                          top: 0,
                          behavior: "smooth",
                        });
                      }

                      if (!prev) {
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }

                      return next;
                    })
                  }
                  className="text-white p-1 rounded-lg hover:bg-red-800 transition-colors btn-hover"
                  title={
                    isLangSearchBarVisible
                      ? lang === "th"
                        ? "ซ่อนรายการตัวอักษร"
                        : "Hide A-Z index"
                      : lang === "th"
                        ? "แสดงรายการตัวอักษร"
                        : "Show A-Z index"
                  }
                  aria-label={
                    isLangSearchBarVisible
                      ? lang === "th"
                        ? "ซ่อนรายการตัวอักษร"
                        : "Hide A-Z index"
                      : lang === "th"
                        ? "แสดงรายการตัวอักษร"
                        : "Show A-Z index"
                  }
                >
                  <AlphabetIndex className="w-9 h-9" />
                </button>
              )}

              <button
                onClick={openSearchPage}
                className="text-white p-1 rounded-lg hover:bg-red-800 transition-colors btn-hover"
                aria-label="Open Search"
                title={lang === "th" ? "ค้นหา" : "Search"}
              >
                <Search
                  className="w-7 h-7"
                  circleFill="#b9483b"
                  circleStroke="currentColor"
                  handleStroke="currentColor"
                />
              </button>
            </div>
          </div>
        </header>

        {/* --- TOGGLED SEARCH BAR (Below Header) --- */}
        {shouldShowSearchBar && (
          <div className="sticky top-14 w-full px-2 py-1.5 bg-white shadow-xl z-20">
            <form
              onSubmit={submitSearch}
              className="relative w-full flex items-center gap-2"
              role="search"
            >
              {/* Search Input Field */}
              <div className="relative flex-1">
                <input
                  type="text"
                  inputMode="search"
                  enterKeyHint="search"
                  placeholder={
                    t.search_placeholder || "Search languages or messages..."
                  }
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="w-full p-2 pl-10 pr-3 text-gray-800 rounded-lg shadow-inner focus:outline-none focus:ring-2 focus:ring-red-300 transition duration-150"
                  style={{ fontSize: "1.1rem" }}
                  autoFocus
                />

                <Search
                  className={`absolute left-2 top-1/2 -translate-y-1/2 w-5 h-5 ${ACCENT_COLOR_CLASS}`}
                />
              </div>

              {/* Clear text button */}
              <button
                type="button"
                onClick={clearSearchAndStayOpen}
                disabled={!searchTerm}
                className={`px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  searchTerm
                    ? "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    : "bg-gray-50 text-gray-300 cursor-not-allowed"
                }`}
                aria-label={t.clear_search || "Clear"}
                title={t.clear_search || "Clear"}
              >
                {t.clear_search || "Clear"}
              </button>

              {/* Close Search Button */}
              <button
                type="button"
                onClick={closeSearchCompletely}
                className="p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label={t.close_search || "Close Search"}
                title={t.close_search || "Close Search"}
              >
                <X className="w-5 h-5" />
              </button>
            </form>
          </div>
        )}

        {/* --- MAIN CONTENT AREA --- */}
        <main ref={mainScrollRef} className="flex-grow overflow-y-auto pb-20">
          {PageContent}
        </main>

        {/* --- AUDIO PLAYER --- */}
        <AudioPlayer
          track={track}
          isMinimized={isAudioMinimized}
          toggleMinimize={toggleAudioMinimize}
          t={t}
          onGoBack={goBack}
          onGoForward={goForward}
          hasPrev={hasPrev}
          hasNext={hasNext}
          onClose={() => setTrack(null)}
        />

        {/* --- NAVIGATION DRAWER (Sidebar) --- */}
        <div
          className={`fixed inset-0 z-40 transition-opacity duration-300 ${
            isDrawerOpen
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 pointer-events-none"
          }`}
        >
          {/* Overlay (Click to close) */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setIsDrawerOpen(false)}
          ></div>

          {/* Drawer Content */}
          <div
            className={`absolute left-0 top-0 w-72 h-full bg-white dark:bg-[#374151] shadow-2xl transition-transform duration-300 transform ${
              isDrawerOpen ? "translate-x-0" : "-translate-x-full"
              // 💡 ADD rounded-tr-xl CLASS HERE
            } rounded-tr-xl flex flex-col`}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {/* Header - Match PWA header height */}
            <div
              className={`${PRIMARY_COLOR_CLASS} px-2 py-1 rounded-r-xl flex-shrink-0 flex items-center justify-between`}
            >
              {/* Left: Logo and Title with Share Button */}
              <div className="flex items-center gap-2">
                {/* 1. The Square Logo */}
                <button
                  onClick={() => {
                    navigateToHome();
                    setIsDrawerOpen(false);
                  }}
                  className="focus:outline-none transition-transform active:scale-95 flex-shrink-0"
                  title={t.home || "Home"}
                >
                  <img
                    src={AppLogo}
                    alt="Logo"
                    className="h-12 w-12 flex-shrink-0 object-cover brightness-125 animate-windy"
                  />
                </button>

                {/* Title Column */}
                <div className="flex flex-col items-start justify-center">
                  {/* 2. App Title */}
                  <h2 className="text-2xl font-bold text-white leading-tight mt-1 mb-1">
                    {t.app_name}
                  </h2>
                </div>
              </div>

              {/* 4. Close Button */}
              <button
                onClick={() => setIsDrawerOpen(false)}
                className="text-white p-1 hover:bg-red-800 rounded-full flex-shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Navigation Links (Scrollable) - Tighter spacing */}
            <nav className="p-4 space-y-0.5 overflow-y-auto flex-grow">
              {/* Navigation Items */}
              {[
                { name: "Search", icon: Search, target: "Search" },
                {
                  name: "Sign_Language",
                  icon: SignLanguage,
                  target: "SignLanguage",
                },
                { name: "Favorites", icon: Heart, target: "Favorites" },
                { name: "Notes", icon: Pen, target: "Notes" },
                {
                  name: "Selected_Messages",
                  icon: Music,
                  target: "SelectedContent",
                },
                { name: "My_Library", icon: Download, target: "MyLibrary" },
                {
                  name: "storage_management",
                  icon: Settings,
                  target: "StorageManagement",
                },
                { name: "Import", icon: Upload, target: "Import" },
                { name: "Feedback", icon: MessageSquare, target: "Feedback" },
                {
                  name: "Share_App_Menu",
                  icon: Share2,
                  target: "Share",
                },
              ].map((item) => {
                // --- NEW: Logic to render a link or a button ---
                if (item.url) {
                  return (
                    <a
                      key={item.name}
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center p-2 rounded-lg font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-[#004d99] transition-colors"
                    >
                      <item.icon className="mr-3 w-6 h-6 shrink-0" />
                      <span className="text-base whitespace-nowrap">
                        {t[item.name.toLowerCase()]}
                      </span>
                    </a>
                  );
                }

                // Original button logic
                const isFavorites = item.name === "Favorites";
                const isNotes = item.name === "Notes";
                const isLibrary = item.name === "My_Library"; // 👈 NEW CHECK

                // Calculate counts safely
                const favoritesCount =
                  (userData?.favorites?.length || 0) +
                  (userData?.favoriteLanguages?.length || 0);

                const notesCount = userData?.notes?.length || 0;
                const libraryCount = offlineTracks.length || 0; // 👈 NEW COUNT

                const count = isFavorites
                  ? favoritesCount
                  : isNotes
                    ? notesCount
                    : isLibrary // 👈 NEW CONDITION
                      ? libraryCount
                      : 0;

                return (
                  <button
                    key={item.name}
                    onClick={() => navigateTo(item.target)}
                    className={`w-full flex items-center justify-between p-2 rounded-lg font-semibold transition-colors ${
                      currentPage.name === item.target
                        ? `${ACCENT_COLOR_CLASS} bg-red-100 dark:bg-red-900/30`
                        : "text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-[#004d99]"
                    }`}
                  >
                    <div className="flex items-center">
                      <item.icon
                        className={`mr-3 w-6 h-6 shrink-0 ${
                          currentPage.name === item.target
                            ? ""
                            : "text-gray-700 dark:text-gray-200"
                        }`}
                      />
                      <div className="flex items-center whitespace-nowrap">
                        <span className="text-base">
                          {t[item.name.toLowerCase()]}
                        </span>
                        {item.target === "SignLanguage" && (
                          <span
                            className="comic-pow shrink-0 inline-flex items-center justify-center bg-[#FF8C00] text-white text-[8px] font-black italic px-1 py-0.5 ml-1 leading-none"
                            style={{ minWidth: "32px", minHeight: "16px" }}
                          >
                            {lang === "th" ? "ใหม่" : "NEW"}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Counter Badge */}
                    {(isFavorites || isNotes || isLibrary) && count > 0 && (
                      <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}

              {/* --- Language Toggle --- */}
              <button
                onClick={() => {
                  const newLang = lang === "th" ? "en" : "th";
                  setLang(newLang);
                  localStorage.setItem("appLang", newLang);
                }}
                className="w-full flex items-center justify-between p-2 rounded-lg font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-[#004d99] transition-colors"
                title={
                  lang === "th" ? "Switch to English" : "เปลี่ยนเป็นภาษาไทย"
                }
              >
                <div className="flex items-center">
                  <Globe className="w-6 h-6 mr-3 shrink-0" />
                  <span className="text-base whitespace-nowrap">
                    {lang === "th" ? "English / ภาษาไทย" : "ภาษาไทย / English"}
                  </span>
                </div>
              </button>

              {/* --- Dark Mode Toggle --- */}
              <button
                onClick={toggleTheme}
                className="w-full flex items-center justify-between p-2 rounded-lg font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-[#004d99] transition-colors"
              >
                <div className="flex items-center">
                  {theme === "dark" ? (
                    <Sun className="w-6 h-6 mr-3 shrink-0" />
                  ) : (
                    <Moon className="w-6 h-6 mr-3 shrink-0" />
                  )}
                  <span className="text-base whitespace-nowrap">
                    {theme === "dark"
                      ? t.light_mode || "Light Mode"
                      : t.dark_mode || "Dark Mode"}
                  </span>
                </div>
              </button>
            </nav>

            {/* Bottom Controls (Sticky) */}
            <div className="p-2 pt-3 border-t border-gray-200 flex-shrink-0 space-y-2">
              {/* --- PWA Share QR Code --- */}
              <div className="">
                {/* Label removed as requested */}
                <div className="flex justify-center">
                  <div
                    className="bg-white p-1.5 rounded shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => {
                      // Create modal for enlarged QR
                      const modal = document.createElement("div");
                      modal.className =
                        "fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4";
                      modal.onclick = () => modal.remove();
                      document.body.appendChild(modal);

                      // Create container with minimal padding
                      const container = document.createElement("div");
                      container.className =
                        "bg-white p-3 rounded-lg shadow-2xl";
                      container.onclick = (e) => e.stopPropagation();

                      // Clone and scale the existing QR code
                      const originalQR =
                        document.querySelector(".sidebar-qr-code");
                      const clonedQR = originalQR.cloneNode(true);
                      clonedQR.setAttribute("width", "256");
                      clonedQR.setAttribute("height", "256");
                      clonedQR.style.display = "block";

                      container.appendChild(clonedQR);
                      modal.appendChild(container);
                    }}
                    title="Click to enlarge"
                  >
                    <QRCodeSVG
                      value={`${window.location.origin}/listen`}
                      size={parseInt(fontSize || "16") * 3.5}
                      level="M"
                      includeMargin={false}
                      className="sidebar-qr-code"
                    />
                  </div>
                </div>
              </div>

              {/* Install Button removed from sidebar (moved to header) */}

              {/* --- Bottom Information --- */}
              <div className="text-xs text-gray-500 dark:text-white dark:bg-[#374151] pt-2 space-y-2">
                {/* Build Information */}
                <div className="text-center space-y-1">
                  <p className="text-gray-600 dark:text-white">
                    Build: {process.env.REACT_APP_BUILD_DATE || "local dev"}
                    {process.env.REACT_APP_VERSION && (
                      <span className="ml-1 opacity-60">
                        (v{process.env.REACT_APP_VERSION})
                      </span>
                    )}
                  </p>
                  <p className="text-gray-600 dark:text-white flex items-center justify-center gap-2">
                    {
                      [...new Set(staticContent.map((item) => item.stableKey))]
                        .length
                    }{" "}
                    Languages | {staticContent.length} Messages | Status
                    <span
                      className={`inline-block w-2 h-2 rounded-full ${
                        isAuthReady
                          ? "bg-green-500 animate-pulse"
                          : "bg-red-500 animate-pulse"
                      }`}
                      title={
                        isAuthReady
                          ? t.auth_ready || "Ready"
                          : t.auth_pending || "Pending"
                      }
                    ></span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* --- INSTALL BANNER --- */}
        {!isPwaInstalled && !isBannerDismissed && (
          <InstallBanner
            onInstall={handleInstallClick}
            t={t}
            lang={lang}
            onClose={() => {
              // Mark as dismissed for this session or logic as needed
              // For now, simpler is better: clicking close removes it from DOM until refresh
              // But InstallBanner handles its own visibility via onClose usually?
              // Wait, InstallBanner component we wrote uses internal visibility logic but we passed onClose.
              // We should probably just store dismissal in a generic state if we want it to persist?
              // The component provided by the thinking block calls `onClose` so we can set a state in App.jsx or let component unmount.
              // Actually, simpler: Let's just have a state `showInstallBanner`?
              // Or better: InstallBanner handles its own "close" visibility?
              // Looking at previous step's code: InstallBanner accepts `onClose`.
              // We can create a state `isBannerDismissed`.
              setIsBannerDismissed(true);
            }}
          />
        )}
      </div>
    ) // This is the closing parenthesis for the entire application UI block
  ); // This is the closing parenthesis for the main return
}
